<!-- Add Payment Method Dialog -->
<div class="add-payment-method-dialog">
  @if (loading()) {
    <div class="loading-overlay">
      <p-progressSpinner strokeWidth="4" />
    </div>
  }

  @if (error()) {
    <p-message severity="error" [text]="error()!" styleClass="mb-3 w-full" />
  }

  <!-- Sticky Tabs Header -->
  <div class="tabs-header">
    <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)">
      <p-tablist>
        <p-tab [value]="0">
          <i class="pi pi-plus mr-2"></i>
          Create New
        </p-tab>
        <p-tab [value]="1">
          <i class="pi pi-download mr-2"></i>
          Load from Gateway
        </p-tab>
      </p-tablist>
    </p-tabs>
  </div>

  <!-- Scrollable Content Area -->
  <div class="dialog-content">
    <!-- Tab 1: Create New -->
    @if (activeTab === 0) {
      <form [formGroup]="createForm" class="form-grid">
        <!-- Required Gateway Fields Section -->
        <div class="section-header full-width">
          <h4>Gateway Information</h4>
        </div>

        <!-- Row 1: Payment Method Type & Gateway Payment Type -->
        <div class="field">
          <label for="type"
            >Payment Method Type <span class="required">*</span></label
          >
          <p-select
            id="type"
            [formControl]="createForm.controls.Type"
            [options]="paymentTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select payment gateway"
            styleClass="w-full"
            appendTo="body"
          />
        </div>

        @if (gatewayPaymentTypeOptions.length > 0) {
          <div class="field">
            <label for="gatewayPaymentType">Gateway Payment Type</label>
            <p-select
              id="gatewayPaymentType"
              [formControl]="createForm.controls.GatewayPaymentType"
              [options]="gatewayPaymentTypeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select payment type"
              styleClass="w-full"
              appendTo="body"
            />
          </div>
        } @else {
          <div class="field"></div>
        }

        <!-- Row 2: Gateway Customer ID & Gateway Payment ID -->
        <div class="field">
          <label for="gatewayCustomerId"
            >Gateway Customer ID <span class="required">*</span></label
          >
          <input
            id="gatewayCustomerId"
            type="text"
            pInputText
            [formControl]="createForm.controls.GatewayCustomerId"
            class="w-full"
            placeholder="e.g., cus_xxxxxxxxxx"
          />
        </div>

        <div class="field">
          <label for="gatewayPaymentId"
            >Gateway Payment ID <span class="required">*</span></label
          >
          <input
            id="gatewayPaymentId"
            type="text"
            pInputText
            [formControl]="createForm.controls.GatewayPaymentId"
            class="w-full"
            placeholder="e.g., pm_xxxxxxxxxx"
          />
        </div>

        <!-- Row 3: Description (full width) -->
        <div class="field full-width">
          <label for="description"
            >Description <span class="required">*</span></label
          >
          <input
            id="description"
            type="text"
            pInputText
            [formControl]="createForm.controls.Description"
            class="w-full"
            placeholder="e.g., Visa ending in 4242"
          />
        </div>

        <!-- Billing Address Section -->
        <div class="section-header full-width">
          <h4>Billing Address (Optional)</h4>
        </div>

        <!-- Row 4: First Name & Last Name -->
        <div class="field">
          <label for="billingFirstName">First Name</label>
          <input
            id="billingFirstName"
            type="text"
            pInputText
            [formControl]="createForm.controls.BillingFirstName"
            class="w-full"
            placeholder="Enter first name"
          />
        </div>

        <div class="field">
          <label for="billingLastName">Last Name</label>
          <input
            id="billingLastName"
            type="text"
            pInputText
            [formControl]="createForm.controls.BillingLastName"
            class="w-full"
            placeholder="Enter last name"
          />
        </div>

        <!-- Row 5: Country & State/Province -->
        <div class="field">
          <label for="billingCountry">Country</label>
          <app-country-select
            [formControl]="createForm.controls.BillingCountry"
            placeholder="Select country"
            [appendTo]="'body'"
          />
        </div>

        <div class="field">
          <label for="billingState">State / Province</label>
          @if (createForm.controls.BillingCountry.value === "US") {
            <app-state-select
              [formControl]="createForm.controls.BillingState"
              placeholder="Select state"
              [appendTo]="'body'"
            />
          } @else {
            <input
              id="billingState"
              type="text"
              pInputText
              [formControl]="createForm.controls.BillingState"
              class="w-full"
              placeholder="Enter state or province"
            />
          }
        </div>

        <!-- Row 6: Street Address (full width) -->
        <div class="field full-width">
          <label for="billingStreet1">Street Address</label>
          <input
            id="billingStreet1"
            type="text"
            pInputText
            [formControl]="createForm.controls.BillingStreet1"
            class="w-full"
            placeholder="Enter street address"
          />
        </div>

        <!-- Row 7: Street Address 2 (full width) -->
        <div class="field full-width">
          <label for="billingStreet2">Street Address 2</label>
          <input
            id="billingStreet2"
            type="text"
            pInputText
            [formControl]="createForm.controls.BillingStreet2"
            class="w-full"
            placeholder="Apt, suite, unit, etc. (optional)"
          />
        </div>

        <!-- Row 8: City & Postal Code -->
        <div class="field">
          <label for="billingCity">City</label>
          <input
            id="billingCity"
            type="text"
            pInputText
            [formControl]="createForm.controls.BillingCity"
            class="w-full"
            placeholder="Enter city"
          />
        </div>

        <div class="field">
          <label for="billingPostcode">Postal Code</label>
          <input
            id="billingPostcode"
            type="text"
            pInputText
            [formControl]="createForm.controls.BillingPostcode"
            class="w-full"
            placeholder="Enter postal code"
          />
        </div>
      </form>
    }

    <!-- Tab 2: Load from Gateway -->
    @if (activeTab === 1) {
      <form [formGroup]="loadForm" class="load-form">
        <!-- Gateway Type -->
        <div class="field">
          <label for="loadType">Gateway <span class="required">*</span></label>
          <p-select
            id="loadType"
            [formControl]="loadForm.controls.Type"
            [options]="getLoadableGateways()"
            optionLabel="label"
            optionValue="value"
            placeholder="Select payment gateway"
            styleClass="w-full"
            appendTo="body"
          />
        </div>

        <!-- Gateway Customer ID -->
        <div class="field">
          <label for="loadGatewayCustomerId"
            >Gateway Customer ID <span class="required">*</span></label
          >
          <input
            id="loadGatewayCustomerId"
            type="text"
            pInputText
            [formControl]="loadForm.controls.GatewayCustomerId"
            class="w-full"
            placeholder="e.g., cus_xxxxxxxxxx"
          />
        </div>

        <!-- Load Button -->
        <div class="field load-button-field">
          <p-button
            label="Load Methods"
            icon="pi pi-download"
            (onClick)="loadFromGateway()"
            [disabled]="!loadForm.valid || loadingExternal()"
            [loading]="loadingExternal()"
          />
        </div>
      </form>

      <!-- External Methods List -->
      @if (externalMethods().length > 0) {
        <div class="external-methods">
          <h4>Available Payment Methods</h4>
          <div class="methods-list-wrapper">
            <div class="methods-list">
              @for (
                method of externalMethods();
                track method.GatewayPaymentId
              ) {
                <div
                  class="method-card"
                  [class.selected]="
                    selectedExternalMethod()?.GatewayPaymentId ===
                    method.GatewayPaymentId
                  "
                  [class.exists]="methodExists(method)"
                  (click)="
                    !methodExists(method) && selectExternalMethod(method)
                  "
                >
                  <div class="method-info">
                    <div class="method-description">
                      {{ method.Description || "Payment Method" }}
                    </div>
                    <div class="method-ids">
                      <span class="label">Payment ID:</span>
                      {{ method.GatewayPaymentId }}
                    </div>
                    @if (method.Expiration) {
                      <div class="method-expiration">
                        Expires: {{ method.Expiration }}
                      </div>
                    }
                  </div>
                  @if (methodExists(method)) {
                    <div class="exists-badge">Already Added</div>
                  } @else if (
                    selectedExternalMethod()?.GatewayPaymentId ===
                    method.GatewayPaymentId
                  ) {
                    <div class="selected-badge">
                      <i class="pi pi-check"></i> Selected
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      } @else if (
        !loadingExternal() && !loadForm.controls.GatewayCustomerId.value
      ) {
        <div class="empty-state">
          <i class="pi pi-info-circle"></i>
          <span>Enter a Gateway Customer ID and click Load Methods</span>
        </div>
      }
    }
  </div>

  <!-- Sticky Footer with Action Buttons -->
  <div class="dialog-footer">
    <p-button
      label="Cancel"
      severity="secondary"
      [outlined]="true"
      (onClick)="cancel()"
      size="small"
      styleClass="min-w-6rem"
    />
    @if (activeTab === 0) {
      <p-button
        label="Add"
        icon="pi pi-plus"
        (onClick)="addNewMethod()"
        [disabled]="!createForm.valid || loading()"
        size="small"
        styleClass="min-w-6rem"
      />
    } @else if (selectedExternalMethod()) {
      <p-button
        label="Add Selected"
        icon="pi pi-plus"
        (onClick)="addSelectedMethod()"
        [disabled]="loading()"
        size="small"
        styleClass="min-w-6rem"
      />
    }
  </div>
</div>
